import Check from "@/assets/icons/check.svg";
import { generateRandomUUID } from "@/lib/utilities";
import { Button } from "@repo/ui/components/ui/button";
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@repo/ui/components/ui/form";
import { cn } from "@repo/ui/lib/utils";
import { useTranslations } from "next-intl";
import type { JSX } from "react";
import {
  type ControllerRenderProps,
  type FieldValues,
  type Path,
  type UseFormReturn,
} from "react-hook-form";

const layouts = {
  default: {
    field: "",
    button:
      "flex h-12 max-md:w-full gap-1 border-transparent px-3 py-4 font-normal max-md:justify-between md:w-auto hover:cursor-pointer relative",
    activeButton: "border-border-selected border-2",
    label: "flex items-center w-full max-md:justify-between gap-2",
    activeLabel: "",
    description: "text-xs font-thin",
  },
  grid: {
    field: "grid gap-4 sm:grid-cols-2 md:grid-cols-3 auto-rows-max",
    button:
      "flex flex-col items-start w-full h-full gap-1 px-3 py-4 text-wrap border-transparent font-normal justify-start hover:cursor-pointer relative",
    activeButton:
      "border-border-selected border-2 py-[calc(1rem-1px)] px-[calc(0.75rem-1px)]",
    label:
      "w-full flex items-center md:justify-center gap-2 whitespace-normal text-left md:text-center",
    activeLabel: "md:-ml-3",
    description: "text-sm font-thin max-md:text-left w-full whitespace-normal",
  },
};

export const FormButtonSelect = <T extends FieldValues>({
  form,
  fieldName,
  fieldOptions,
  multi = false,
  className,
  classNameItem,
  classNameButton,
  classNameLabel,
  classNameFormItem,
  classNameButtonDescription,
  actionAfterChange,
  layout = "default",
  customError,
  icon,
  checkPosition = "right",
  disabled = false,
}: {
  form: UseFormReturn<T, unknown, T>;
  fieldName: Path<T>;
  fieldOptions: {
    label: string | JSX.Element;
    value: string;
    description?: string;
  }[];
  multi?: boolean;
  className?: string;
  classNameItem?: string;
  classNameButton?: string;
  classNameLabel?: string;
  classNameFormItem?: string;
  classNameButtonDescription?: string;
  actionAfterChange?: (value: string) => void;
  layout?: keyof typeof layouts;
  customError?: string;
  icon?: JSX.Element;
  checkPosition?: "left" | "right";
  disabled?: boolean;
}) => {
  const t = useTranslations();

  const handleChange = (
    field: ControllerRenderProps<T, Path<T>>,
    value: string
  ) => {
    if (multi) {
      if (!field.value) field.onChange([value]);
      else {
        field.value?.includes(value)
          ? field.onChange(field.value?.filter((v: string) => v !== value))
          : field.onChange([...field.value, value]);
      }
    } else {
      field.onChange(value);
    }

    form.trigger(fieldName);
    actionAfterChange?.(value);
  };

  const isActive = ({ value, option }: { value?: string; option: string }) => {
    if (multi) {
      return value?.includes(option);
    } else {
      return value === option;
    }
  };

  const ButtonContent = ({
    active,
    option,
  }: {
    active?: boolean;
    option: {
      label: string | JSX.Element;
      value: string;
      description?: string;
    };
  }) => {
    return (
      <>
        <div
          className={cn(
            styling.label,
            active && styling.activeLabel,
            classNameLabel
          )}
        >
          {checkPosition === "left" ? (
            <Label active={active} option={option.label} />
          ) : (
            <>
              {option.label}
              {active && (
                <Check className="text-icon inline-block h-4 w-4 my-1 shrink-0" />
              )}
            </>
          )}
        </div>
        {option.description && (
          <span className={cn(styling.description, classNameButtonDescription)}>
            {option.description}
          </span>
        )}
      </>
    );
  };

  const Label = ({
    active,
    option,
  }: {
    active?: boolean;
    option: string | JSX.Element;
  }) =>
    layout === "grid" ? (
      <span>
        {active && (
          <Check className="text-icon mr-2 inline-block h-4 w-4 my-1 shrink-0" />
        )}
        {option}
      </span>
    ) : (
      <>
        {active && (
          <Check className="text-icon inline-block h-4 w-4 max-md:hidden my-1 shrink-0" />
        )}
        {option}
        {active && (
          <Check className="text-icon h-4 w-4 md:hidden my-1 shrink-0" />
        )}
      </>
    );

  const styling = layouts[layout];

  return (
    <FormField<T, Path<T>>
      control={form.control}
      name={fieldName}
      render={() => (
        <FormItem className={cn("w-full", classNameFormItem)}>
          <FormControl>
            <div
              className={cn(
                "flex w-full flex-col flex-wrap gap-x-2 gap-y-2 md:gap-y-4 md:flex-row",
                styling.field,
                className
              )}
            >
              {fieldOptions.map((option, index) => (
                <FormField<T, Path<T>>
                  key={`${option.value}-${generateRandomUUID()}`}
                  control={form.control}
                  name={fieldName}
                  render={({ field }) => {
                    const active = isActive({
                      value: field.value,
                      option: option.value,
                    });

                    return (
                      <FormItem
                        key={option.value}
                        className={cn(
                          "flex w-full flex-col items-start space-x-3 space-y-0 md:w-auto md:flex-row",
                          classNameItem
                        )}
                      >
                        <FormControl>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleChange(field, option.value)}
                            className={cn(
                              styling.button,
                              active && styling.activeButton,
                              classNameButton
                            )}
                            type="button"
                            data-pw={`${fieldName}-${index}`}
                            disabled={disabled}
                          >
                            {icon ? (
                              <>
                                <span className="mr-2">{icon}</span>
                                <div className="text-left">
                                  <ButtonContent
                                    active={active}
                                    option={option}
                                  />
                                </div>
                              </>
                            ) : (
                              <ButtonContent active={active} option={option} />
                            )}
                          </Button>
                        </FormControl>
                      </FormItem>
                    );
                  }}
                />
              ))}
            </div>
          </FormControl>
          {customError ? (
            <p className="text-destructive mb-6 text-sm font-medium">
              {customError}
            </p>
          ) : (
            <FormMessage t={t} />
          )}
        </FormItem>
      )}
    />
  );
};
